import { DRIVERS, LUMERY_BROTHER_IDS } from '../../data/drivers';
import { LOCAL_DRIVERS } from '../../data/localExport';

// 24/07/2026 — catalogue combiné : chauffeurs FR + viviers locaux export.
const findDriverData = (dataId) =>
  DRIVERS.find(d => d.id === dataId) || LOCAL_DRIVERS.find(d => d.id === dataId);
import { generateId } from '../helpers';
import { evaluateTrainingReaction, isAmbitious } from '../../data/personalityTraits';
import { getDriverSpecialties, getSpecialtyCostMultiplier } from '../../utils/driverSpecialties';
import { computeSeveranceCost, formatSeveranceSummary } from '../helpers/severance';
import { buildDriverArchiveEntry } from '../helpers/driverArchive';
import { runDailyXpTick, canDriverAskForRaiseAfterLevelup } from '../helpers/driverXpTick';
import { runMonthlyAttrition } from '../helpers/driverAttritionTick';
import { TRAININGS_CATALOGUE, TRAINING_XP_BY_TYPE } from '../../data/driverTrainings';
import { tStandalone } from '../../i18n';

// XP thresholds per level (cumulative XP needed to BE at that level)
const XP_THRESHOLDS = [0, 100, 300, 600, 1000, 1500];
const getXpForLevel = (level) => XP_THRESHOLDS[level] || XP_THRESHOLDS[XP_THRESHOLDS.length - 1] + (level - XP_THRESHOLDS.length + 1) * 600;
// Starting cumulative XP for a freshly-hired driver of given level. A level-3
// driver should already sit at the level-3 threshold so the bar reflects their
// actual progress toward level 4, not 0 / 600.
const getStartingXp = (level) => XP_THRESHOLDS[Math.max(0, level - 1)] || 0;

export const createHRSlice = (set, get) => ({
  drivers: [],

  // 02/06/2026 — Ledger of every fired-driver event (rupture conventionnelle,
  // licenciement, période d'essai). Used by the annual report to compute the
  // "Coût RH évitable" KPI (sum of all severance.total this year) so the
  // player sees the cumulative cost of staff turnover.
  // Shape: Array<{ day, year, driverName, mode, total, breakdown }>
  severanceLedger: [],

  // Map of driverDataId → { unavailableUntil: totalDays, reason: 'rejected_low_offer', expectedSalary }
  // Used to lock a candidate out of the recruitment pool when they've recently
  // refused an offer. Cleared by tickDrivers when the cooldown expires.
  driverNegotiations: {},

  /**
   * Compute the candidate's expected gross salary based on their base
   * employerCost. Anchored by a deterministic per-driver seed so the player
   * sees a stable expectation across renders (no jitter on each click).
   *
   * Trait adjustments :
   *   ambitieux → +10 % expectation
   *   pragmatique → -5 % expectation
   *   fier → +5 % expectation (proud profiles ask for recognition)
   *   loyal → -5 % (will accept slightly lower from a reputable boss)
   *   opportuniste → +10 % (sees the company can afford more when known)
   *
   * 05/02/2026 — Q1 retour user prod : influence société.
   * Réputation > 60 → -5 % (boîte attractive, candidats acceptent moins).
   * Réputation < 30 → +10 % (mauvaise rep = prime de risque demandée).
   * Notoriété > 50 → -3 % (boîte connue, fierté).
   * Notoriété < 20 → +5 % (inconnue → risque pour candidat).
   */
  computeExpectedSalary: (driverDataId) => {
    const state = get();
    const data = findDriverData(driverDataId);
    if (!data) return 0;
    // Deterministic seed: hash the dataId so the same driver always asks for
    // the same amount within a game. (Simple charcode hash modulo 21 → -10 % to +10 %).
    let seed = 0;
    for (let i = 0; i < driverDataId.length; i++) seed = (seed + driverDataId.charCodeAt(i)) % 211;
    const variation = (seed / 211) * 0.20 - 0.05; // ranges -5 % → +15 %
    let mult = 1 + variation;
    const traits = data.personalityTraits || [];
    if (traits.includes('ambitieux')) mult += 0.10;
    if (traits.includes('fier')) mult += 0.05;
    if (traits.includes('pragmatique')) mult -= 0.05;
    if (traits.includes('loyal')) mult -= 0.05;
    if (traits.includes('opportuniste')) mult += 0.10;

    // 05/02/2026 — Influence société (réputation + notoriété).
    const reputation = state.reputation || 50;
    const notoriety = state.notoriety || 0;
    if (reputation >= 60) mult -= 0.05;
    else if (reputation < 30) mult += 0.10;
    if (notoriety >= 50) mult -= 0.03;
    else if (notoriety < 20) mult += 0.05;

    // 22/07/2026 — chauffeur débauché chez un concurrent : déjà en poste,
    // il exige une prime pour bouger (+15 %, cf. driverPoolSlice.poachDriver).
    const _neg = (state.driverNegotiations || {})[driverDataId];
    if (_neg && _neg.poachPremium) mult += _neg.poachPremium;

    return Math.round(data.salaryGross * mult);
  },

  /**
   * Attempt to hire a driver with a specific salary offer. Returns:
   *   { ok: true, satisfactionBonus: 10|0|-10 }  — accepts
   *   { ok: false, reason: 'unavailable', until: N } — locked out
   *   { ok: false, reason: 'offer_too_low', expected: N, lockUntil: N } — refuses (insulting offer)
   *
   * 05/02/2026 — Q1 retour user prod : ajout d'un round de contre-
   * proposition unique. Le candidat propose un compromis au lieu de
   * refuser net sur la fourchette 80 %-99 % :
   *   { ok: false, reason: 'counter_offer', counterSalary, expected }
   * Le joueur peut alors :
   *   • Accepter en proposant >= counterSalary → embauché.
   *   • Refuser en restant en dessous → rupture définitive + cooldown.
   * Un seul round est autorisé : si le joueur a déjà reçu une
   * contre-offre, son nouvel `offer` est interprété comme la réponse
   * finale.
   */
  proposeHireDriver: (driverDataId, offeredGrossSalary) => {
    const state = get();
    const data = findDriverData(driverDataId);
    if (!data) return { ok: false, reason: 'not_found' };

    // Already on payroll?
    if (state.drivers.find(d => d.dataId === driverDataId)) {
      return { ok: false, reason: 'already_hired' };
    }

    // Cooldown after a previous refusal?
    const neg = (state.driverNegotiations || {})[driverDataId];
    if (neg && neg.unavailableUntil && neg.unavailableUntil > state.totalDays) {
      return { ok: false, reason: 'unavailable', until: neg.unavailableUntil };
    }

    const expected = get().computeExpectedSalary(driverDataId);
    const offer = Math.round(offeredGrossSalary || 0);
    const ratio = offer / expected;

    // ── ROUND 2 : réponse à une contre-proposition déjà émise ───────────
    if (neg && neg.counterOffer) {
      if (offer >= neg.counterOffer) {
        // Joueur accepte (ou surpaye) la contre-proposition → embauche.
        const finalSalary = offer;
        const employerCostFactor = finalSalary / data.salaryGross;
        // Léger malus satisfaction (a fallu négocier) sauf si bien au-dessus.
        const satisfactionBonus = offer >= expected ? 5 : 0;
        get().hireDriver(driverDataId, {
          salaryGross: finalSalary,
          employerCost: Math.round(data.employerCost * employerCostFactor),
          satisfactionBonus,
        });
        const next = { ...state.driverNegotiations };
        delete next[driverDataId];
        set({ driverNegotiations: next });
        return { ok: true, hiredSalary: finalSalary, satisfactionBonus, viaCounter: true };
      } else {
        // Joueur n'a pas suivi la contre-proposition → rupture définitive.
        const cooldown = 60 + Math.floor(Math.random() * 31); // 60-90 jours
        set({
          driverNegotiations: {
            ...state.driverNegotiations,
            [driverDataId]: {
              unavailableUntil: state.totalDays + cooldown,
              reason: 'counter_refused',
              expectedSalary: expected,
              lastOfferedAt: state.totalDays,
              poachPremium: neg?.poachPremium,
              poachedFrom: neg?.poachedFrom,
            },
          },
          notifications: [...(state.notifications || []).slice(-30), {
            id: generateId(),
            type: 'hire_rejected',
            title: 'Négociation rompue',
            message: `${data.name} a refusé votre dernière offre (${offer.toLocaleString('fr-FR')} € vs sa contre-proposition ${neg.counterOffer.toLocaleString('fr-FR')} €). Indisponible ${cooldown} j.`,
            day: state.totalDays,
            read: false,
          }],
        });
        return { ok: false, reason: 'counter_refused', expected, counterSalary: neg.counterOffer, lockUntil: state.totalDays + cooldown };
      }
    }

    // ── ROUND 1 : Refus net si offre < 80 % (insultant) ─────────────────
    if (ratio < 0.80) {
      const cooldown = 45 + Math.floor(Math.random() * 46); // 45 - 90 days
      set({
        driverNegotiations: {
          ...state.driverNegotiations,
          [driverDataId]: {
            unavailableUntil: state.totalDays + cooldown,
            reason: 'rejected_low_offer',
            expectedSalary: expected,
            lastOfferedAt: state.totalDays,
            poachPremium: neg?.poachPremium,
            poachedFrom: neg?.poachedFrom,
          },
        },
        notifications: [...(state.notifications || []).slice(-30), {
          id: generateId(),
          type: 'hire_rejected',
          title: 'Candidat parti',
          message: `${data.name} a refusé votre offre (${offer.toLocaleString('fr-FR')} € vs ${expected.toLocaleString('fr-FR')} € attendu) et n'est plus disponible avant ${cooldown} jours.`,
          day: state.totalDays,
          read: false,
        }],
      });
      return { ok: false, reason: 'offer_too_low', expected, lockUntil: state.totalDays + cooldown };
    }

    // 05/02/2026 — Q1 retour user prod : contre-proposition si 80 %-99 %.
    // Le candidat propose un compromis à mi-chemin (90 % de l'attendu)
    // avec une légère influence trait : un "pragmatique" coupe à 88 %,
    // un "ambitieux" / "fier" tient à 95 % minimum.
    if (ratio < 1.0) {
      let counterFactor = 0.90;
      const traits = data.personalityTraits || [];
      if (traits.includes('ambitieux') || traits.includes('fier')) counterFactor = 0.95;
      else if (traits.includes('pragmatique') || traits.includes('loyal')) counterFactor = 0.88;
      // La contre-proposition est toujours au-dessus de l'offre du joueur
      // (sinon le joueur a déjà gagné — on accepte directement).
      const counterSalary = Math.max(offer + 50, Math.round(expected * counterFactor));
      // On enregistre la contre-proposition (single round).
      set({
        driverNegotiations: {
          ...state.driverNegotiations,
          [driverDataId]: {
            counterOffer: counterSalary,
            expectedSalary: expected,
            lastOfferedAt: state.totalDays,
            playerOffer: offer,
            poachPremium: neg?.poachPremium,
            poachedFrom: neg?.poachedFrom,
          },
        },
      });
      return {
        ok: false,
        reason: 'counter_offer',
        counterSalary,
        expected,
        playerOffer: offer,
      };
    }

    // ── Accepté ──────────────────────────────────────────────────────────
    let satisfactionBonus = 0;
    let employerCostFactor = 1;
    if (ratio >= 1.05) {
      satisfactionBonus = 10;
      employerCostFactor = offer / data.salaryGross; // higher gross, higher charges
    } else if (ratio >= 0.90) {
      satisfactionBonus = 0;
      employerCostFactor = offer / data.salaryGross;
    } else {
      // 0.80 ≤ ratio < 0.90 — accepts but resentful
      satisfactionBonus = -10;
      employerCostFactor = offer / data.salaryGross;
    }

    // Delegate to hireDriver with the negotiated salary overrides.
    get().hireDriver(driverDataId, {
      salaryGross: offer,
      employerCost: Math.round(data.employerCost * employerCostFactor),
      satisfactionBonus,
    });

    // Clear any stale negotiation entry.
    if (neg) {
      const next = { ...state.driverNegotiations };
      delete next[driverDataId];
      set({ driverNegotiations: next });
    }

    return { ok: true, satisfactionBonus, hiredSalary: offer };
  },

  hireDriver: (driverDataId, overrides = {}) => {
    const state = get();
    const driverData = findDriverData(driverDataId);
    if (!driverData) return;

    // Restore the driver's training history if they were previously employed
    // (fired or resigned) and rehired now. firedDriversArchive is keyed by
    // dataId so each "Karim Belhadj" comes back with whatever they learned
    // before. Costs the player a small reintegration premium (handled in UI).
    const archive = (state.firedDriversArchive || {})[driverData.id];

    const hired = {
      id: generateId(),
      dataId: driverData.id,
      name: driverData.name,
      level: archive?.level || driverData.level,
      specialty: archive?.specialty || driverData.specialty,
      specialty2: archive?.specialty2 || driverData.specialty2 || null,
      extraSpecialties: archive?.extraSpecialties || [],
      personalityTraits: driverData.personalityTraits || [],
      competence: archive?.competence || driverData.competence,
      licenses: archive?.licenses || driverData.licenses || ['B'],
      experience: driverData.experience || 0,
      fatigueMax: driverData.fatigueMax || 80,
      accidentRisk: archive?.accidentRisk || driverData.accidentRisk || 0.1,
      strength: driverData.strength || '',
      weakness: driverData.weakness || '',
      // 24/07/2026 — recrutement local export : tag pays du vivier local.
      ...(driverData.localCountry ? { localCountry: driverData.localCountry } : {}),
      fatigue: 10,
      // Rehired drivers come back slightly more loyal (gratitude bump).
      // Negotiation bonus stacks on top when proposeHireDriver delegates here.
      satisfaction: Math.max(0, Math.min(100, (archive ? 70 : 65) + (overrides.satisfactionBonus || 0))),
      status: 'idle',
      assignedVehicleId: null,
      // Honour the negotiated salary if provided.
      // 08/02/2026 — Q2 Future/Backlog : prime de réembauche.
      // Si l'archive existe ET aucun override de salaire fourni (ré-embauche
      // simple "ré-engager" depuis l'onglet Anciens chauffeurs, pas via la
      // négo), on applique +5 % au dernier salaire connu (REHIRE_PREMIUM).
      // Si override fourni (négo classique), on respecte l'offre du joueur.
      salaryGross:
        overrides.salaryGross
        || (archive?.salaryGross ? Math.round(archive.salaryGross * 1.05) : driverData.salaryGross),
      employerCost:
        overrides.employerCost
        || (archive?.employerCost ? Math.round(archive.employerCost * 1.05) : driverData.employerCost),
      hiredAt: state.totalDays,
      trainings: archive?.trainings || [],
      totalTrips: archive?.totalTrips || 0,
      totalKm: archive?.totalKm || 0,
      xp: archive?.xp || getStartingXp(driverData.level),
      xpToNext: getXpForLevel(archive?.level || driverData.level),
      rehired: !!archive,
      // 17/07/2026 (user prod) — Cumul mensuel : les nouvelles recrues
      // démarrent à 0 j et cumulent +2,5 j au 1er de chaque mois
      // (applyMonthlyHolidayAccrual). Fini les 25 j d'un coup.
      holidayDaysRemaining: 0,
      holidayDaysAccruedYear: 0,
      holidayDaysTakenYear: 0,
      onHoliday: false,
      holidayEndDay: null,
      holidayStartDay: null,
    };

    // Clear the archive for this dataId — they're back on payroll, the next
    // fire-then-rehire cycle will produce a fresh archive snapshot.
    const newArchive = { ...(state.firedDriversArchive || {}) };
    if (archive) delete newArchive[driverData.id];

    set({
      drivers: [...state.drivers, hired],
      firedDriversArchive: newArchive,
      notifications: archive ? [
        ...(state.notifications || []).slice(-30),
        {
          id: generateId(),
          type: 'driver_rehired',
          title: 'Retour de chauffeur',
          message: `${hired.name} retrouve son poste. Formations conservées (Niv.${hired.level}, ${(hired.trainings || []).length} formation${(hired.trainings || []).length > 1 ? 's' : ''}).`,
          day: state.totalDays,
          read: false,
        },
      ] : (state.notifications || []),
    });

    // 31/05/2026 — Nathalie reactive follow-up (only for SENIORS, lvl ≥ 5).
    try {
      if ((hired.level || 0) >= 5) {
        const fu = require('../helpers/nathalieEventFollowup').buildSeniorHireFollowup(
          get(),
          hired,
        );
        if (fu && typeof get().enqueueDeferredMessage === 'function') {
          get().enqueueDeferredMessage(
            (state.totalDays || 0) + 1,
            fu,
            `nathalie-senior-${hired.id}`,
          );
        }
      }
    } catch (err) { console.warn('[hrSlice] hire follow-up failed:', err); }

    // 14/06/2026 — Clin d'œil narratif (validé utilisateur) : Ouss Hervé,
    // le conseiller taquin, commente l'arrivée d'un frère Lumery d'une
    // pique sarcastique. Message spécial quand le duo est complet, avec
    // rappel du bonus fratrie (−10 % fatigue au même dépôt).
    if (LUMERY_BROTHER_IDS.includes(hired.dataId) && typeof get().pushCustomMessage === 'function') {
      const bothHired = LUMERY_BROTHER_IDS.every(id => get().drivers.some(d => d.dataId === id));
      const firstName = hired.name.split(' ')[0];
      get().pushCustomMessage(bothHired ? {
        senderType: 'advisor',
        senderName: 'Ouss Hervé',
        tone: 'sarcastic',
        kind: 'info',
        subject: 'La fratrie Lumery au complet — rien que ça',
        body: `Alors comme ça, on collectionne les Lumery. ${firstName} qui signe, et voilà la fratrie réunie sous votre toit. Je vous l'accorde : deux phénomènes pareils, ça ne se refuse pas. Petit conseil d'un vieux qui a tout vu : mettez-les au MÊME dépôt. Ces deux-là se tirent vers le haut — comptez −10 % de fatigue chacun. Séparez-les, et vous paierez deux salaires de star pour deux chauffeurs qui boudent. Ce serait dommage, même pour vous.`,
        ttlDays: 14,
      } : {
        senderType: 'advisor',
        senderName: 'Ouss Hervé',
        tone: 'sarcastic',
        kind: 'info',
        subject: 'Un Lumery dans votre écurie ?',
        body: `${firstName} Lumery chez vous ? Je dois avouer que je ne vous en croyais pas capable. Ces gens-là ne signent pas chez n'importe qui — votre réputation a dû lui faire oublier la taille de votre garage. Au fait, il a un frère. Aussi doué que lui, paraît-il. Mais bon, je ne vais pas vous apprendre votre métier... quoique.`,
        ttlDays: 14,
      });
    }
  },

  fireDriver: (driverId, options = {}) => {
    const state = get();
    const driver = state.drivers.find(d => d.id === driverId);
    if (!driver) return { ok: false, reason: 'not_found' };

    // Block firing if the driver is currently on a mission (vehicle is active
    // on a contract). The player must resiliate the contract first (with the
    // 25% penalty) to free the driver.
    if (driver.assignedVehicleId) {
      const vehicle = state.vehicles.find(v => v.id === driver.assignedVehicleId);
      if (vehicle && vehicle.status === 'active') {
        const notification = {
          id: generateId(),
          type: 'fire_blocked',
          title: 'Licenciement impossible',
          message: `${driver.name} est actuellement en mission. Résiliez d'abord le contrat (pénalité 25%) avant de pouvoir licencier le chauffeur.`,
          day: state.totalDays,
          read: false,
        };
        set({ notifications: [...(state.notifications || []).slice(-30), notification] });
        return { ok: false, reason: 'on_mission' };
      }
    }

    // 02/06/2026 — Coût réaliste : rupture conventionnelle par défaut
    // (ISRC + charges sur ancienneté > 8 mois), période d'essai pour les
    // chauffeurs récents (< 8 mois), licenciement classique (mode='fault')
    // si appelé explicitement par un événement futur.
    const mode = options.mode === 'fault' ? 'fault' : 'rupture';
    const severance = computeSeveranceCost(driver, state.totalDays, mode);
    const severanceCost = severance.total;

    // Snapshot training history into the archive keyed by dataId so a
    // future rehire restores everything they learned.
    // 08/02/2026 — Centralisé via buildDriverArchiveEntry (inclut désormais
    // salaryGross pour la prime de réembauche +5 %, REHIRE_PREMIUM).
    const archive = buildDriverArchiveEntry(driver, state.totalDays);

    // Push an audit notification with the breakdown so the player understands
    // why the treasury was debited by this amount.
    const titleKey =
      severance.mode === 'probation' ? 'fire_notif_probation'
      : severance.mode === 'fault' ? 'fire_notif_fault'
      : 'fire_notif_rupture';
    const notification = {
      id: generateId(),
      type: 'driver_fired',
      title: tStandalone(titleKey),
      message: formatSeveranceSummary(severance, driver.name).replace(/\*\*/g, ''),
      day: state.totalDays,
      read: false,
    };

    set({
      drivers: state.drivers.filter(d => d.id !== driverId),
      vehicles: state.vehicles.map(v =>
        v.assignedDriverId === driverId ? { ...v, assignedDriverId: null, status: 'idle' } : v
      ),
      treasury: state.treasury - severanceCost,
      firedDriversArchive: {
        ...(state.firedDriversArchive || {}),
        [driver.dataId]: archive,
      },
      notifications: [...(state.notifications || []).slice(-30), notification],
      // 02/06/2026 — Append to severance ledger for the annual "Coût RH
      // évitable" KPI. We cap the history at 200 entries to bound memory.
      severanceLedger: [
        ...(state.severanceLedger || []).slice(-199),
        {
          day: state.totalDays,
          year: state.year,
          driverName: driver.name,
          driverDataId: driver.dataId,
          mode: severance.mode,
          total: severance.total,
          breakdown: severance.breakdown,
        },
      ],
    });
    return { ok: true, severanceCost, severance };
  },

  assignDriverToVehicle: (driverId, vehicleId) => {
    const state = get();
    const driver = state.drivers.find(d => d.id === driverId);
    const previousVehicleId = driver?.assignedVehicleId;
    set({
      drivers: state.drivers.map(d =>
        d.id === driverId ? { ...d, assignedVehicleId: vehicleId, status: 'active' } : d
      ),
      vehicles: state.vehicles.map(v => {
        if (v.id === vehicleId) return { ...v, assignedDriverId: driverId };
        if (v.id === previousVehicleId) return { ...v, assignedDriverId: null };
        return v;
      }),
    });
  },

  unassignDriver: (driverId) => {
    const state = get();
    const driver = state.drivers.find(d => d.id === driverId);
    if (!driver || !driver.assignedVehicleId) return;
    set({
      drivers: state.drivers.map(d =>
        d.id === driverId ? { ...d, assignedVehicleId: null, status: 'idle' } : d
      ),
      vehicles: state.vehicles.map(v =>
        v.assignedDriverId === driverId ? { ...v, assignedDriverId: null } : v
      ),
    });
  },

  /**
   * 11/06/2026 (soir 9) — Manually send a driver on holiday (5-day block).
   * Used when the player wants to plan rest proactively. Requires at least
   * 5 days banked in the driver's holidayDaysRemaining balance.
   *
   * If the driver is currently assigned to a vehicle on an active contract,
   * the contract will start missing deliveries unless the player enables
   * `useInterim` on that vehicle (see toggleVehicleInterim).
   */
  sendDriverOnHoliday: (driverId) => {
    const state = get();
    const driver = state.drivers.find(d => d.id === driverId);
    if (!driver) return { ok: false, reason: 'not_found' };
    if (driver.onHoliday) return { ok: false, reason: 'already_on_holiday' };
    if ((driver.holidayDaysRemaining || 0) < 5) return { ok: false, reason: 'insufficient_balance' };
    // 05/02/2026 — Q1 retour user prod : un salarié venant d'arriver ne
    // pose pas de congés dans les 2 premiers mois (~60 jours).
    const totalDaysNow = state.totalDays || 0;
    const hiredAt = driver.hiredAt || 0;
    if (totalDaysNow - hiredAt < 60) {
      return {
        ok: false,
        reason: 'newcomer_lock',
        daysUntilEligible: 60 - (totalDaysNow - hiredAt),
      };
    }
    const totalDays = state.totalDays || 0;
    // 10/02/2026 (Lot L) — Retour user prod : « lors d'un congés, l'intérim
    // ne semble pas couvrir l'absence du chauffeur, le client est donc
    // mécontent ». Si le chauffeur part en congés sur un véhicule en
    // mission active, on auto-active `useInterim` sur ce véhicule pour
    // que le contrat continue (avec coût quotidien d'intérim facturé).
    // Marqué `interimAutoActivated` pour pouvoir le retirer auto au retour.
    let autoInterimVehicleId = null;
    const vehicleOfDriver = driver.assignedVehicleId
      ? state.vehicles.find(v => v.id === driver.assignedVehicleId)
      : null;
    const onActiveMission = vehicleOfDriver
      && vehicleOfDriver.status === 'active'
      && !!vehicleOfDriver.assignedContractId;
    if (onActiveMission && !vehicleOfDriver.useInterim) {
      autoInterimVehicleId = vehicleOfDriver.id;
    }
    set({
      drivers: state.drivers.map(d => d.id === driverId ? {
        ...d,
        onHoliday: true,
        status: 'holiday',
        // 10/02/2026 (Lot N P1) — Marqueur de planification pour la
        // tolérance client : un congé planifié via le panneau ou la
        // décision manuelle du joueur est tolérée plus largement
        // (CA -3 % au-delà de 5 j vs -10 % pour les auto-trigger).
        holidayPlanned: true,
        holidayStartDay: totalDays,
        holidayEndDay: totalDays + 5,
        holidayDaysRemaining: Math.max(0, (d.holidayDaysRemaining || 0) - 5),
        holidayDaysTakenYear: Math.round(((d.holidayDaysTakenYear || 0) + 5) * 10) / 10,
      } : d),
      // Apply the auto-interim flag if eligible.
      vehicles: autoInterimVehicleId
        ? state.vehicles.map(v => v.id === autoInterimVehicleId
            ? { ...v, useInterim: true, interimAutoActivated: true }
            : v)
        : state.vehicles,
      notifications: [...(state.notifications || []).slice(-30), {
        id: generateId(),
        type: 'driver_holiday_start',
        title: 'Départ en congés',
        message: autoInterimVehicleId
          ? `${driver.name} part en congés pour 5 jours — intérim activé automatiquement sur le véhicule (solde restant : ${Math.max(0, (driver.holidayDaysRemaining || 0) - 5)} j).`
          : `${driver.name} part en congés pour 5 jours (solde restant : ${Math.max(0, (driver.holidayDaysRemaining || 0) - 5)} j).`,
        day: totalDays,
        read: false,
      }],
    });
    return { ok: true, autoInterimVehicleId };
  },

  /**
   * 13/06/2026 — Compte Épargne Temps (CET) : rachat de jours de congés.
   * Retour utilisateur : « La pression des congés est trop importante,
   * peut-on envisager un compte épargne entreprise où on achète des
   * congés ? J'ai l'impression de tourner h24 avec de l'intérim. »
   *
   * L'entreprise monétise 5 jours du solde du chauffeur (indemnité
   * compensatrice = coût employeur / 21,7 j ouvrés × 5 j × 1,10 de
   * majoration CET). Le chauffeur encaisse → +3 satisfaction. Le solde
   * baisse de 5 j → la pression congés diminue d'autant.
   * Plafond légal gameplay : 10 jours rachetés / chauffeur / an.
   */
  /**
   * 04/02/2026 — Phase Q1 (retour user prod) : choix CET fin d'année.
   *
   * Au jour 350 de chaque année, ce trigger émet UN message decision par
   * chauffeur avec `holidayDaysRemaining > 0`. Le joueur choisit :
   *   (1) Paiement immédiat en bulletin de décembre (effet `holidayPayoff`)
   *   (2) Report sur le Compte Épargne Temps (effet `cetTransfer`)
   *
   * Le message expire 14 jours plus tard ; la réponse par défaut est le
   * report CET (option respectueuse du salarié).
   */
  triggerEndOfYearCetChoice: () => {
    const state = get();
    if (typeof state.addMessage !== 'function') return { ok: false, reason: 'messaging_unavailable' };
    const today = state.totalDays || 0;
    let emitted = 0;
    (state.drivers || []).forEach((d) => {
      const days = d.holidayDaysRemaining || 0;
      if (days <= 0) return;
      const dailyCost = (d.employerCost || d.salaryGross || 2800) / 21.7;
      const payoff = Math.round(dailyCost * days);
      state.addMessage({
        kind: 'decision',
        sender: 'Équipe RH',
        title: `Bilan congés fin d'année — ${d.name}`,
        body:
          `${d.name} a ${days} jour${days > 1 ? 's' : ''} de congés non posé${days > 1 ? 's' : ''} cette année.\n\n` +
          `Au 31 décembre, la loi française vous laisse deux choix :\n` +
          `1) Paiement en bulletin de décembre : ${payoff.toLocaleString('fr-FR')} € (satisfaction +5).\n` +
          `2) Report sur Compte Épargne Temps : gratuit, le salarié récupèrera les jours plus tard ou les monétisera à son départ.\n\n` +
          `Choisissez avant le 14 janvier — sans réponse, le report CET sera appliqué d'office.`,
        driverId: d.id,
        day: today,
        deadline: today + 14,
        defaultChoiceId: 'cet',
        choices: [
          {
            id: 'pay',
            label: `Payer ${payoff.toLocaleString('fr-FR')} €`,
            effects: [{ kind: 'holidayPayoff' }],
          },
          {
            id: 'cet',
            label: 'Reporter sur CET',
            effects: [{ kind: 'cetTransfer' }],
          },
        ],
      });
      emitted += 1;
    });
    return { ok: true, emitted };
  },

  buyBackHolidayDays: (driverId) => {
    const state = get();
    const driver = state.drivers.find(d => d.id === driverId);
    if (!driver) return { ok: false, reason: 'not_found' };
    if (driver.onHoliday) return { ok: false, reason: 'on_holiday' };
    if ((driver.holidayDaysRemaining || 0) < 5) return { ok: false, reason: 'insufficient_balance' };
    const boughtThisYear = driver.holidayDaysBoughtThisYear || 0;
    if (boughtThisYear + 5 > 10) return { ok: false, reason: 'yearly_cap_reached' };
    const monthlyCost = driver.employerCost || driver.salaryGross || 2800;
    const cost = Math.round((monthlyCost / 21.7) * 5 * 1.10);
    if ((state.treasury || 0) < cost) return { ok: false, reason: 'insufficient_treasury', cost };
    set({
      treasury: state.treasury - cost,
      drivers: state.drivers.map(d => d.id === driverId ? {
        ...d,
        holidayDaysRemaining: Math.max(0, (d.holidayDaysRemaining || 0) - 5),
        holidayDaysBoughtThisYear: boughtThisYear + 5,
        satisfaction: Math.min(100, (d.satisfaction || 0) + 3),
      } : d),
      notifications: [...(state.notifications || []).slice(-30), {
        id: generateId(),
        type: 'driver_holiday_buyback',
        title: 'CET — congés rachetés',
        message: `${driver.name} monétise 5 jours de congés via le Compte Épargne Temps (−${cost.toLocaleString('fr-FR')} €, satisfaction +3). Solde restant : ${Math.max(0, (driver.holidayDaysRemaining || 0) - 5)} j.`,
        day: state.totalDays,
        read: false,
      }],
    });
    return { ok: true, cost };
  },

  /**
   * 11/06/2026 — Toggle the interim driver on a given vehicle. When ON,
   * if the titular driver is on holiday the contract keeps running but a
   * daily interim cost is charged (see getInterimDailyCost).
   */
  toggleVehicleInterim: (vehicleId, enabled) => {
    const state = get();
    set({
      vehicles: state.vehicles.map(v =>
        v.id === vehicleId ? { ...v, useInterim: enabled === undefined ? !v.useInterim : !!enabled } : v
      ),
    });
  },

  giveBonus: (driverId) => {
    const state = get();
    if (state.treasury < 150) return;
    set({
      drivers: state.drivers.map(d =>
        d.id === driverId ? { ...d, satisfaction: Math.min(100, d.satisfaction + 8) } : d
      ),
      treasury: state.treasury - 150,
    });
  },

  forceRest: (driverId) => {
    const state = get();
    set({
      drivers: state.drivers.map(d =>
        d.id === driverId ? { ...d, fatigue: Math.max(0, d.fatigue - 25), status: 'resting' } : d
      ),
    });
  },

  trainDriver: (driverId, trainingType) => {
    const state = get();
    const training = TRAININGS_CATALOGUE[trainingType];
    if (!training) return { ok: false, reason: 'unknown_training' };

    const driver = state.drivers.find(d => d.id === driverId);
    if (!driver || driver.status === 'training') return { ok: false, reason: 'busy' };

    // Block training when the driver is on a mission.
    if (driver.assignedVehicleId) {
      const vehicle = state.vehicles.find(v => v.id === driver.assignedVehicleId);
      if (vehicle && vehicle.status === 'active') return { ok: false, reason: 'on_mission' };
    }

    // PERSONALITY CHECK — hard refusal blocks the training; soft malus is
    // applied later as a satisfaction penalty.
    const reaction = evaluateTrainingReaction(driver, trainingType);
    if (reaction.refused) {
      return { ok: false, reason: 'personality_refuse', trait: reaction.trait, label: reaction.label };
    }

    // 05/02/2026 — Prérequis licence pour habilitation exceptionnelle.
    if (training.requiresPermis) {
      const driverLicenses = Array.isArray(driver.licenses) ? driver.licenses : (driver.license ? [driver.license] : []);
      if (!driverLicenses.includes(training.requiresPermis)) {
        return { ok: false, reason: 'missing_prerequisite_permis', requires: training.requiresPermis };
      }
    }

    // Specialty trainings: only refuse if the driver already has THIS specialty.
    // Slot cap removed — cost grows for slot 3+ via getSpecialtyCostMultiplier.
    let effectiveCost = training.cost;
    if (training.giveSpecialty) {
      const existing = getDriverSpecialties(driver);
      if (existing.includes(training.giveSpecialty)) {
        return { ok: false, reason: 'spec_already_owned' };
      }
      const mult = getSpecialtyCostMultiplier(existing.length);
      effectiveCost = Math.round(training.cost * mult);
    }

    if (state.treasury < effectiveCost) return { ok: false, reason: 'insufficient_funds' };

    // XP bonus from training (specialty trainings give the highest boosts).
    // Catalogue extracted to data/driverTrainings.js (11/02/2026).
    const trainingXp = TRAINING_XP_BY_TYPE;

    set({
      drivers: state.drivers.map(d => {
        if (d.id !== driverId) return d;
        const newLicenses = [...(d.licenses || ['B'])];
        if (training.givePermis && !newLicenses.includes(training.givePermis)) {
          newLicenses.push(training.givePermis);
        }
        // Apply specialty grant: fill specialty if empty, else specialty2,
        // else push into extraSpecialties[].
        let newSpecialty = d.specialty;
        let newSpecialty2 = d.specialty2;
        let newExtras = [...(d.extraSpecialties || [])];
        if (training.giveSpecialty) {
          if (!newSpecialty) newSpecialty = training.giveSpecialty;
          else if (!newSpecialty2) newSpecialty2 = training.giveSpecialty;
          else newExtras = [...newExtras, training.giveSpecialty];
        }
        // 04/02/2026 — Phase F1 : sensibilisations ajoutent une certification.
        const newCertifications = [...(d.certifications || [])];
        if (training.giveCertification && !newCertifications.includes(training.giveCertification)) {
          newCertifications.push(training.giveCertification);
        }
        const newXp = (d.xp || 0) + (trainingXp[trainingType] || 15);
        // Apply soft-malus from personality: -8 satisfaction.
        const malusPenalty = reaction.malus ? 8 : 0;
        // 02/06/2026 — Medical visit (visite d'aptitude) is NON-BLOCKING.
        // IRL it's a half-day appointment, the driver can resume work the
        // same day. We renew the HSE anchor (via the trainings[] push) but
        // do NOT change status / trainingEndDay, so the modal+side panels
        // never lock the driver. Player feedback (02/06/2026): "Ca a fait
        // bugger une salarié elle pouvait plus rien faire, rendre ça juste
        // informatif".
        const isNonBlocking = trainingType === 'medical';
        const baseUpdate = {
          ...d,
          competence: Math.min(5, d.competence + (training.boostCompetence || 0)),
          satisfaction: Math.max(0, Math.min(100, d.satisfaction + (training.boostSatisfaction || 0) - malusPenalty)),
          licenses: newLicenses,
          specialty: newSpecialty,
          specialty2: newSpecialty2,
          extraSpecialties: newExtras,
          certifications: newCertifications,
          trainings: [...(d.trainings || []), { type: trainingType, name: training.name, startDay: state.totalDays }],
          xp: newXp,
        };
        if (isNonBlocking) return baseUpdate;
        return {
          ...baseUpdate,
          status: 'training',
          trainingEndDay: state.totalDays + training.days,
          trainingType: trainingType,
        };
      }),
      treasury: state.treasury - effectiveCost,
    });
    return { ok: true, malus: !!reaction.malus, trait: reaction.trait, label: reaction.label, cost: effectiveCost, nonBlocking: trainingType === 'medical' };
  },

  // Add daily XP to active drivers (called from nextDay).
  // - Active + on mission   → +12 XP/day (base 6 + 6 mission)
  // - Active + assigned but vehicle idle → +6 XP/day
  // - Idle/resting (no vehicle) → +3 XP/day (passive experience)
  // - Training → no XP from here (training already grants a fixed bonus)
  // Tuning (21/05/2026): bumped from 2/5/8 → 3/6/12 so level-ups feel reachable
  // (the user feedback was "toujours pas d'évolution des niveaux"). Thresholds
  // unchanged to preserve test stability and overall balance.
  addDailyXp: (working) => {
    const state = get();
    if (!working) return;

    // Pure XP/level-up computation extracted to driverXpTick helper
    // (11/02/2026 — refactor hrSlice >1000 lignes).
    const { updatedDrivers, levelUps } = runDailyXpTick(state, {
      getXpForLevel,
      getStartingXp,
    });

    if (levelUps.length === 0) {
      set({ drivers: updatedDrivers });
      return;
    }

    const newNotifs = levelUps.map(lu => ({
      id: `lvl-${lu.id}-${state.totalDays}`,
      type: 'driver_levelup',
      title: 'Montée en niveau !',
      message: `${lu.name} passe au niveau ${lu.newLevel} — +0.2 compétence, +5 satisfaction, salaire +4 %.`,
      day: state.totalDays,
      read: false,
    }));
    set({
      drivers: updatedDrivers,
      notifications: [...(state.notifications || []).slice(-30), ...newNotifs],
    });

    // For each level-up, roll a "raise request" message into the inbox.
    // Probability: 25 % base, raised to 45 % for 'ambitieux' drivers.
    // Anti-harassment gates (see canDriverAskForRaiseAfterLevelup) :
    //   1. Hard floor 90 j between two consecutive requests.
    //   2. Decision-based cooldown 180 j (accepted) / 60 j (refused).
    //   3. Skip if salary > 150 % of computed baseline.
    if (typeof get().pushMessageFromTemplate !== 'function') return;

    const _today = state.totalDays;
    levelUps.forEach(lu => {
      const _drv = updatedDrivers.find(d => d.id === lu.id);
      if (!_drv) return;
      const _baseline = typeof get().computeExpectedSalary === 'function'
        ? get().computeExpectedSalary(_drv.dataId)
        : (_drv.salary || 0);
      if (!canDriverAskForRaiseAfterLevelup(_drv, _today, _baseline)) return;
      const baseProb = isAmbitious(_drv) ? 0.45 : 0.25;
      if (Math.random() < baseProb) {
        set({
          drivers: get().drivers.map(d =>
            d.id === _drv.id ? { ...d, lastRaiseRequestDay: _today } : d
          ),
        });
        get().pushMessageFromTemplate('drv-raise-after-levelup-01', {
          driverId: lu.id,
          name: lu.name,
          level: lu.newLevel,
        });
      }
    });
  },

  /**
   * Monthly attrition check (called from coreSlice.nextDay on newDay === 1).
   * Drivers with low satisfaction may resign. On-mission drivers are SAFE
   * (loyalty until the mission is over); only idle / resting / training drivers
   * can quit. The `turnover_reduction` support bonus cuts the probability.
   *
   * Returns an array of resigned-driver records so the caller can patch the
   * vehicle assignments + show notifications. The function performs no `set`
   * by itself — the caller composes the resign-then-update transaction.
   */
  rollMonthlyAttrition: () => {
    const state = get();
    const turnoverReduction = (typeof state.getSupportTurnoverReduction === 'function')
      ? state.getSupportTurnoverReduction() : 0;

    const result = runMonthlyAttrition(state, {
      turnoverReduction,
      buildArchiveEntry: buildDriverArchiveEntry,
      generateId,
    });

    if (result.count === 0) return { count: 0 };
    set(result.patch);
    return { count: result.count, resigned: result.resigned };
  },
});
